<template>
    <div v-editable="page" class="yougov-about-page">
        <div class="about-content-arrangement">
            <div v-if="loading" class="about-loader">
                <LoadingDots />
            </div>
            <ContentContainer v-else :default-styling="true">
                <h1 class="text-alignment">{{ pageTitle }}</h1>
                <div v-for="(item, key, index) in page.widgetsData" :key="key">
                    <AboutSection
                        :enable-larger-font-size="index === 0"
                        :content="item.text"
                        :imageurl="item.image"
                        :imageposition="item.imagePosition"
                        class="about-section"
                    />
                </div>
            </ContentContainer>
        </div>
        <AboutFooter />
    </div>
</template>

<script>
import { ErrorCodes, StorySlug } from '@/enums';
import ContentContainer from '@/components/ContentContainer';
import AboutSection from '@/components/AboutSection';
import AboutFooter from '@/components/AboutFooter';
import LoadingDots from '@/components/LoadingDots';
import ContentService from '@/services/content.service';
import AbTestService from '@/services/ab-test.service';
import PageMixin from '@/mixins/PageMixin';

export default {
    name: 'YouGovAbout',
    components: {
        ContentContainer,
        AboutSection,
        AboutFooter,
        LoadingDots,
    },
    mixins: [PageMixin],
    async asyncData(context) {
        try {
            const abTestService = new AbTestService(context.app.$cookies);
            const contentService = new ContentService(context.app.$storyapi, abTestService, context.$logger);
            const story = await contentService.getStory({
                story: StorySlug.About,
                version: context.app.$storyblokHelper.version,
                locale: context.app.i18n.locale,
                hasFallback: true,
            });

            const page = await contentService.getPageContent(story, ['media_objects']);

            if (page.errors.length) {
                page.errors.map((item) =>
                    context.$logger.error({
                        msg: 'error.getPageContent About',
                        item,
                        errorCode: ErrorCodes.FailedToGetPageContent,
                    }),
                );
                context.$notifications.error(context.app.i18n.t('notification.pageLoadFailed'), { dismissable: true });
            }

            return {
                page,
                loading: false,
                pageTitle: story.content.title,
            };
        } catch (error) {
            context.$logger.error({ msg: 'error.aboutContent', error });
            context.$notifications.error(context.app.i18n.t('notification.pageLoadFailed'), { dismissable: true });
        }
    },
    data() {
        return {
            page: { widgetsData: {}, sections: { media_objects: { widgets: [] } } },
            pageTitle: '',
            loading: true,
        };
    },
    mounted() {
        this.$_pageMixin_toggleChatVisibility(true);
        const abTestService = new AbTestService(this.$cookies);
        this.contentService = new ContentService(this.$storyapi, abTestService, this.$logger);
        this.$storyblokHelper.addEventListener('input', this.onStoryblokInput);
    },
    beforeDestroy() {
        this.$storyblokHelper.removeEventListener('input', this.onStoryblokInput);
    },
    methods: {
        async onStoryblokInput(event) {
            if (event.story.id === this.page.id) {
                const page = await this.contentService.getPageContent(event.story, ['media_objects']);
                this.page = page;
                this.pageTitle = event.story.content.title;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.yougov-about-page {
    display: flex;
    flex-direction: column;
}

.about-content-arrangement {
    flex: 1 1 auto;
}

.text-alignment {
    text-align: center;
    padding-bottom: 18px;
}

.about-section {
    margin-bottom: 50px;

    @include media-query(medium) {
        margin-bottom: 30px;
    }
}

.about-loader {
    margin-top: -40px;
    @include center();
    position: absolute;
    ::v-deep .dots {
        color: $about-link-button;
    }
}
</style>
